import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cmsService, PublicCmsSettings } from "../../services/cmsService";
import Footer from "../../components/layout/Footer";

const Contact: React.FC = () => {
  const [cmsSettings, setCmsSettings] = useState<PublicCmsSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCmsSettings();
  }, []);

  const loadCmsSettings = async () => {
    try {
      const settings = await cmsService.getPublicSettings();
      setCmsSettings(settings);
    } catch (err) {
      console.error("Failed to load CMS settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const siteName = cmsSettings?.siteName || "متجرنا";

  return (
    <div className="contact-page bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
      {/* Mobile Top App Bar */}
      <div className="md:hidden flex items-center bg-background-light dark:bg-background-dark p-4 pb-2 justify-between sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-[#0d171b] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
          اتصل بنا
        </h2>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-background-light dark:bg-background-dark border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 py-4">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            اتصل بنا
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12 flex-1">
        <div className="max-w-4xl mx-auto">
          {/* About Section */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#0d171b] dark:text-white">
              من نحن
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 md:p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                {cmsSettings?.siteDescription || 
                  "نحن متجر إلكتروني متخصص في تقديم أفضل المنتجات لعملائنا في جميع أنحاء الجزائر. نسعى دائماً لتوفير تجربة تسوق ممتازة وخدمة عملاء متميزة."}
              </p>
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                نؤمن بأن رضاكم هو أولويتنا، ونعمل بجد لضمان حصولكم على أفضل المنتجات بأفضل الأسعار.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#0d171b] dark:text-white">
              معلومات الاتصال
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Social Media Links */}
              {(cmsSettings?.facebookUrl || 
                cmsSettings?.instagramUrl || 
                cmsSettings?.twitterUrl || 
                cmsSettings?.youtubeUrl || 
                cmsSettings?.whatsappNumber) && (
                <div className="bg-white dark:bg-slate-800 rounded-xl p-6 md:p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
                  <h3 className="text-lg md:text-xl font-semibold mb-4 text-[#0d171b] dark:text-white">
                    وسائل التواصل الاجتماعي
                  </h3>
                  <div className="space-y-3">
                    {cmsSettings?.facebookUrl && (
                      <a
                        href={cmsSettings.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-slate-600 dark:text-slate-400 hover:text-primary transition"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span>Facebook</span>
                      </a>
                    )}
                    {cmsSettings?.instagramUrl && (
                      <a
                        href={cmsSettings.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-slate-600 dark:text-slate-400 hover:text-primary transition"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                        <span>Instagram</span>
                      </a>
                    )}
                    {cmsSettings?.twitterUrl && (
                      <a
                        href={cmsSettings.twitterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-slate-600 dark:text-slate-400 hover:text-primary transition"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                        <span>Twitter</span>
                      </a>
                    )}
                    {cmsSettings?.youtubeUrl && (
                      <a
                        href={cmsSettings.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-slate-600 dark:text-slate-400 hover:text-primary transition"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        <span>YouTube</span>
                      </a>
                    )}
                    {cmsSettings?.whatsappNumber && (
                      <a
                        href={`https://wa.me/${cmsSettings.whatsappNumber.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-slate-600 dark:text-slate-400 hover:text-primary transition"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span>WhatsApp: {cmsSettings.whatsappNumber}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Methods */}
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 md:p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-[#0d171b] dark:text-white">
                  طرق التواصل
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-[#0d171b] dark:text-white">البريد الإلكتروني</p>
                      {cmsSettings?.email ? (
                        <a
                          href={`mailto:${cmsSettings.email}`}
                          className="text-slate-600 dark:text-slate-400 hover:text-primary transition"
                        >
                          {cmsSettings.email}
                        </a>
                      ) : (
                        <p className="text-slate-600 dark:text-slate-400">info@{siteName.toLowerCase().replace(/\s+/g, '')}.com</p>
                      )}
                    </div>
                  </div>
                  {cmsSettings?.storeLocation && (
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="font-semibold text-[#0d171b] dark:text-white">موقع المتجر</p>
                        {(() => {
                          // Check if it's GPS coordinates (format: "lat,lng" or "lat, lng")
                          const gpsPattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
                          const isGPS = gpsPattern.test(cmsSettings.storeLocation.trim());
                          
                          if (isGPS) {
                            const [lat, lng] = cmsSettings.storeLocation.split(',').map(s => s.trim());
                            const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
                            return (
                              <div>
                                <p className="text-slate-600 dark:text-slate-400 mb-1">
                                  {cmsSettings.storeLocation}
                                </p>
                                <a
                                  href={googleMapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  عرض على الخريطة
                                </a>
                              </div>
                            );
                          } else {
                            // It's an address, create a search link
                            const googleMapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cmsSettings.storeLocation)}`;
                            return (
                              <div>
                                <p className="text-slate-600 dark:text-slate-400 mb-1">
                                  {cmsSettings.storeLocation}
                                </p>
                                <a
                                  href={googleMapsSearchUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  البحث على الخريطة
                                </a>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-[#0d171b] dark:text-white">الهاتف</p>
                      <p className="text-slate-600 dark:text-slate-400">متوفر من خلال وسائل التواصل الاجتماعي</p>
                    </div>
                  </div>
                  {!cmsSettings?.storeLocation && (
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <p className="font-semibold text-[#0d171b] dark:text-white">الموقع</p>
                        <p className="text-slate-600 dark:text-slate-400">جميع أنحاء الجزائر</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-8 md:p-12 border border-primary/20">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#0d171b] dark:text-white">
                هل لديك سؤال؟
              </h2>
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mb-6">
                لا تتردد في التواصل معنا عبر وسائل التواصل الاجتماعي أو تصفح منتجاتنا
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/catalog"
                  className="inline-block bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition shadow-lg shadow-primary/30"
                >
                  تصفح المنتجات
                </Link>
                <Link
                  to="/categories"
                  className="inline-block border-2 border-primary text-primary px-6 py-3 rounded-xl font-bold hover:bg-primary/10 transition"
                >
                  تصفح الفئات
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;

